import React, { useEffect, useMemo, useRef, useState } from 'react'

import { getErrorMessage } from 'utils'
import { useEncodingDevice } from 'contexts'
import { api } from 'services'

import {
  ArrowLeftIcon,
  PackageIcon,
  RadioReceiverIcon,
  SmartphoneNfcIcon,
  TriangleAlertIcon,
} from 'lucide-react'

import { useTheme, Box, Checkbox, CircularProgress, Radio, Stack, Typography } from '@mui/material'

import { Button, Card, Modal, Alert, Skeleton } from '@repo/ui'
import { EmptyState, PACKAGE_HANDLE_URL_RE, Search, SegmentedTabs, PackageCell } from 'components'

import { usePackages } from 'hooks/api'
import { useLinkPackagesToOrder } from 'hooks/api/useShopifyOrdersQuery'
import type { ShopifyOrder } from 'store/orders/ordersStore'

interface LinkPackageModalProps {
  order?: ShopifyOrder | null
  open: boolean
  onClose?: () => void
  refetch?: () => void
  /**
   * VRG-138 Phase 1 (legacy — prefer lineItemIds): when set, scopes this
   * link to a specific order line item instead of the order as a whole.
   * Omitted => order-level link (the pre-existing bulk "Link Packages"
   * header action).
   */
  lineItemId?: string
  /**
   * VRG-138 Phase 2: checkbox-grouping / shipment model — the set of line
   * item ids the user selected on the order-detail Products list. When set,
   * the chosen package is linked to EVERY id in this array. Takes
   * precedence over `lineItemId`.
   */
  lineItemIds?: string[]
  /**
   * VRG-138 Phase 2: restricts package selection to exactly ONE package (a
   * package IS a box IS a shipping label — a group of selected products is
   * added to a single box, not several). Renders a radio instead of a
   * checkbox per package row. Defaults to false (existing multi-select
   * behaviour, used by the order-level bulk-link flow on OrdersPage).
   */
  singleSelect?: boolean
  /** Optional title override — e.g. "Add to shipping label" for the grouping flow. */
  title?: string
  /**
   * VRG-138: a small step badge rendered next to the title (e.g. "1/2") — used by
   * the per-product / bulk step-through so the N-of-M count lives in a chip
   * rather than being crammed into the title string.
   */
  badge?: string
  /**
   * VRG-138 Phase 1: called after a successful link INSTEAD of auto-closing —
   * used by the bulk "step through items" flow to advance to the next
   * unlinked item without unmounting the modal. When omitted, a successful
   * link closes the modal as before.
   */
  onLinked?: () => void
  /**
   * VRG-140 Slice 2: which selection UX this instance renders — `true` = the
   * new scan-driven flow (scan a package's handle label to resolve + link
   * it), `false` = the legacy manual radio/checkbox list. Defaults to
   * `singleSelect` — the "Add to shipping label" grouping flow (OrderDetailPage)
   * is scan-driven; the order-level bulk-link flow (OrdersPage) keeps the
   * pre-existing manual list so its usage isn't disrupted. Pass explicitly to
   * override that default.
   */
  scanMode?: boolean
  /**
   * VRG-138: bulk / per-product "step through" flow. When set, an extra advance
   * button renders in the actions (advance without closing), and a successful
   * link also advances (via onLinked). The 1/N badge is passed through `title`.
   */
  onNext?: () => void
  /** Label for the onNext button (e.g. "Next Product", "Next Order"). */
  nextLabel?: string
  /** Step back to the previous product in the per-product step-through. */
  onBack?: () => void
  /**
   * VRG-138: a product preview rendered above the scan area — used by the
   * per-product step-through (Orders kebab) so the user sees which product the
   * scanned package is being assigned to.
   */
  productPreview?: React.ReactNode
  /**
   * VRG-138: the product this scan is expected to be packing (the order line
   * item). When set, each scanned package's product is compared against it and
   * a mismatch surfaces a soft warning (the package contents are known first,
   * so we can catch "wrong box for this product" before assigning). Assignment
   * is still allowed — it's a warning, not a hard block.
   */
  expectedProduct?: { title?: string | null; sku?: string | null }
}

/** One package resolved from a scanned handle this modal session. */
interface ScannedPackage {
  id: string
  name: string
  quantity?: number | null
  sku?: string | null
  /** The raw resolved package, for the shared PackageCell preview. */
  pkg?: any
  /** The package's own product title (for the mismatch warning copy). */
  productTitle?: string | null
  /** True when the package's product doesn't match `expectedProduct`. */
  mismatch?: boolean
}

export const LinkPackageModal = (props: LinkPackageModalProps) => {
  const {
    open,
    order,
    onClose = () => {},
    refetch = () => {},
    lineItemId,
    lineItemIds,
    singleSelect = false,
    title = 'Link packages',
    onLinked,
    onNext,
    nextLabel,
    onBack,
    productPreview,
    badge,
    expectedProduct,
  } = props
  const theme = useTheme()

  // VRG-140 Slice 2: the "Add to shipping label" grouping flow (singleSelect) is
  // scan-driven by default; the order-level bulk-link flow (OrdersPage) keeps the
  // pre-existing manual list unless a caller explicitly opts in.
  const scanMode = props.scanMode ?? singleSelect

  // ---- legacy manual-list state (scanMode === false) ----
  const [linkMethod, setLinkMethod] = useState<'manual' | 'nfc'>('manual')
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // ---- scan-driven state (scanMode === true) ----
  const { isConnected: readerConnected, readerName, helperReachable, subscribeScan } = useEncodingDevice()
  const readerReady = helperReachable && readerConnected
  // TODO(VERA-455): wire phone-QR package-handle scan when VERA-455 mobile-scan lands.
  // Reader-only for now; the 'phone' method renders a "coming soon" stub below.
  const [scanMethod, setScanMethod] = useState<'reader' | 'phone'>('reader')
  const [scannedPackages, setScannedPackages] = useState<ScannedPackage[]>([])
  const [scanStatus, setScanStatus] = useState<{ message: string | null; severity: 'info' | 'error' }>(
    { message: null, severity: 'info' },
  )
  const [isResolvingScan, setIsResolvingScan] = useState(false)
  // Guards against the reader firing more than one scan event for the same tap
  // before the first resolve completes (mirrors CreateCertificatesModal's
  // packageScanResolvingRef).
  const scanResolvingRef = useRef(false)
  const scannedIdsRef = useRef<Set<string>>(new Set())
  // Dev-only simulate-scan input (VRG-140 Slice 2 Batch 2) — lets the
  // resolve→checklist→link path be exercised without physical hardware.
  const [simulateValue, setSimulateValue] = useState('')

  // Reset local state whenever the modal opens/closes, the target line item(s)
  // change, OR the target ORDER changes — the last is needed for the bulk
  // "step through orders" flow (VRG-138), where the modal stays mounted and
  // only `order` changes as you advance, so the previous order's scanned
  // packages must clear.
  React.useEffect(() => {
    setLinkMethod('manual')
    setSearch('')
    setSelectedIds(new Set())
    setScannedPackages([])
    scannedIdsRef.current = new Set()
    setScanStatus({ message: null, severity: 'info' })
    setIsResolvingScan(false)
    scanResolvingRef.current = false
    setSimulateValue('')
    setScanMethod('reader')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lineItemId, lineItemIds, order?.id])

  const { data: packagesData, isLoading: isLoadingPackages } = usePackages(
    { search, limit: 100 },
    { enabled: open && !scanMode },
  )

  // TODO(VERA-891): filter packages by order company once usePackages supports a companyId param
  const packages = useMemo(() => {
    const all = packagesData?.data ?? []
    const companyId = order?.company?.id
    if (!companyId) return all
    return all.filter((pkg) => pkg.company_id === companyId)
  }, [packagesData, order])

  const linkPackages = useLinkPackagesToOrder()

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      if (singleSelect) {
        // VRG-138 Phase 2: a package IS a box IS a shipping label — only one
        // may be chosen. Clicking the already-selected package deselects it.
        return prev.has(id) ? new Set() : new Set([id])
      }
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleClose = () => {
    onClose()
  }

  // VRG-140 Slice 2: resolve a scanned package-handle URL (`/pkg/<packages.id>`) —
  // same regex + resolution path as CreateCertificatesModal's package-handle quick
  // path (`GET /packages/:id`). Adds the resolved package to the running checklist.
  const resolveScannedUrl = async (scannedUrl: string) => {
    if (scanResolvingRef.current) return
    const match = PACKAGE_HANDLE_URL_RE.exec(scannedUrl)
    if (!match) return
    const packageId = match[1]

    if (scannedIdsRef.current.has(packageId)) {
      setScanStatus({ message: "You've already scanned this package.", severity: 'info' })
      return
    }

    scanResolvingRef.current = true
    setIsResolvingScan(true)
    setScanStatus({ message: 'Reading package…', severity: 'info' })
    try {
      // `api.get` resolves even on 4xx (the axios interceptor never rejects for a
      // known error response) — check `status` explicitly.
      const response = await api.get(`/packages/${packageId}`)
      const status = response?.status
      const pkg = response?.data
      if (!pkg?.id || (typeof status === 'number' && (status < 200 || status >= 300))) {
        setScanStatus({ message: "That package label isn't recognized", severity: 'error' })
        return
      }

      const orderCompanyId = order?.company?.id
      if (orderCompanyId && pkg.company_id && pkg.company_id !== orderCompanyId) {
        setScanStatus({
          message: `${pkg.name} belongs to a different company and can't be linked to this order.`,
          severity: 'error',
        })
        return
      }

      // The package's own product (known first — the box was registered to a
      // product). Compare it to the order line item we're packing; a mismatch is
      // a soft warning (assignment still allowed).
      const pkgProduct = pkg.tag_shop_product
      const pkgSku = pkgProduct?.selected_variant?.sku ?? pkgProduct?.sku ?? null
      const pkgProductTitle = pkgProduct?.selected_variant?.title ?? pkgProduct?.title ?? null
      const expectedSku = expectedProduct?.sku ?? null
      const mismatch = Boolean(expectedSku && pkgSku && expectedSku !== pkgSku)

      scannedIdsRef.current.add(packageId)
      setScannedPackages((prev) => [
        ...prev,
        {
          id: pkg.id,
          name: pkg.name,
          quantity: pkg.quantity ?? null,
          sku: pkgSku,
          pkg,
          productTitle: pkgProductTitle,
          mismatch,
        },
      ])
      setScanStatus({ message: null, severity: 'info' })
    } catch (err) {
      setScanStatus({ message: getErrorMessage(err), severity: 'error' })
    } finally {
      setIsResolvingScan(false)
      scanResolvingRef.current = false
    }
  }

  const handleRemoveScanned = (id: string) => {
    scannedIdsRef.current.delete(id)
    setScannedPackages((prev) => prev.filter((p) => p.id !== id))
  }

  // Stream USB-reader scans (a package handle's plain NDEF URL) into the resolver
  // above, only while the reader method is selected — mirrors ScanSessionBlock's
  // per-method subscription gating.
  useEffect(() => {
    if (!open || !scanMode || scanMethod !== 'reader') return
    const unsub = subscribeScan((scannedUrl) => {
      resolveScannedUrl(scannedUrl)
    })
    return () => {
      unsub()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scanMode, scanMethod, subscribeScan, order])

  // VRG-140 Slice 2 Batch 2: dev-only affordance that feeds a pasted package id
  // or `/pkg/<id>` URL through the SAME resolver a real reader/phone scan would
  // use — so the resolve→checklist→link path is verifiable without hardware.
  // Never rendered outside dev / the dev-session preview build.
  const handleSimulateScan = () => {
    const value = simulateValue.trim()
    if (!value) return
    const url = PACKAGE_HANDLE_URL_RE.test(value) ? value : `https://dev.local/pkg/${value}`
    resolveScannedUrl(url)
    setSimulateValue('')
  }

  const handleLinkPackages = async () => {
    if (!order?.id || linkPackages.isPending) return
    const packageIds = scanMode ? Array.from(scannedIdsRef.current) : Array.from(selectedIds)
    if (packageIds.length === 0) return
    try {
      await linkPackages.mutateAsync({
        orderId: order.id,
        packageIds,
        lineItemId,
        lineItemIds,
      })
    } catch (err) {
      // Surface a rejected assignment (e.g. the package belongs to a different
      // company) INSTEAD of silently closing — the mutation now throws on a
      // non-2xx response (see useLinkPackagesToOrder).
      setScanStatus({ message: getErrorMessage(err), severity: 'error' })
      return
    }
    refetch()
    if (onLinked) {
      onLinked()
    } else {
      handleClose()
    }
  }

  const selectedCount = scanMode ? scannedPackages.length : selectedIds.size

  return (
    <Modal
      title={title}
      titleAdornment={
        badge ? (
          <Box
            sx={{
              px: 2,
              py: 0.25,
              borderRadius: 10,
              bgcolor: theme.palette.primary[50],
              border: `1px solid ${theme.palette.primary[100]}`,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {badge}
            </Typography>
          </Box>
        ) : undefined
      }
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={linkPackages.isPending}
          >
            Cancel
          </Button>
          {/* Step back to the previous product (per-product step-through). */}
          {onBack && (
            <Button
              disableElevation
              variant="secondary"
              onClick={onBack}
              disabled={linkPackages.isPending}
              startIcon={<ArrowLeftIcon strokeWidth={1} size={16} />}
            >
              Back
            </Button>
          )}
          {/* Step-through (bulk orders / per-product): advance without linking. */}
          {onNext && (
            <Button
              disableElevation
              variant="secondary"
              onClick={onNext}
              disabled={linkPackages.isPending}
            >
              {nextLabel ?? 'Next Order'}
            </Button>
          )}
          <Button
            disableElevation
            onClick={handleLinkPackages}
            disabled={
              (!scanMode && linkMethod === 'nfc') || selectedCount === 0 || linkPackages.isPending
            }
            loading={linkPackages.isPending}
          >
            {singleSelect
              ? 'Assign package'
              : `Link${selectedCount > 0 ? ` ${selectedCount}` : ''} package${selectedCount === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      {scanMode ? (
        <Stack spacing={4}>
          {/* Per-product step-through (Orders kebab): the product this scan will
              assign a package to, shown above the scanner so the operator knows
              which of the order's products they're packing. */}
          {productPreview && (
            <Stack spacing={2}>
              <Typography variant="body3" color="text.secondary">
                Product
              </Typography>
              {productPreview}
            </Stack>
          )}
          {/* Reader-arm card — mirrors the Packages-page AddPackagesModal scanner:
              a single reader status row, no method tabs. Assignment RESOLVES the
              tapped (already-registered) package and links it; it never rewrites
              the tag's handle. */}
          {linkPackages.isError && (
            <Alert severity="error" description={getErrorMessage(linkPackages.error)} />
          )}

          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            sx={{
              border: `1px solid ${theme.palette.primary[100]}`,
              borderRadius: 2,
              px: 4,
              py: 3,
            }}
          >
            <RadioReceiverIcon
              strokeWidth={1}
              color={readerReady ? theme.palette.brand[500] : theme.palette.text.disabled}
            />
            <Stack spacing={0.5} sx={{ flex: 1 }}>
              <Typography variant="body3">
                {readerReady
                  ? readerName || 'Reader connected'
                  : helperReachable
                    ? 'No reader detected'
                    : 'Reader helper not running'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {readerReady
                  ? "Ready — tap this product's box on the reader to assign it."
                  : 'Start the Vera Reader Helper and plug in the reader to continue.'}
              </Typography>
            </Stack>
            {/* Right side: a steady "Connected" indicator while idle, replaced by
                a spinner only during an active read (never both at once). */}
            {isResolvingScan ? (
              <CircularProgress size={20} thickness={4} sx={{ color: theme.palette.text.disabled }} />
            ) : (
              readerReady && (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: theme.palette.success.main,
                    }}
                  />
                  <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 500 }}>
                    Connected
                  </Typography>
                </Stack>
              )
            )}
          </Stack>

          {scanStatus.message && (
            <Alert severity={scanStatus.severity} description={scanStatus.message} />
          )}

          <Stack spacing={2}>
            <Typography variant="body3" color="text.secondary">
              Package
            </Typography>
            {scannedPackages.length === 0 ? (
              <Stack
                alignItems="center"
                spacing={2}
                sx={{
                  border: `1px solid ${theme.palette.primary[100]}`,
                  borderRadius: 2,
                  py: 6,
                  px: 4,
                }}
              >
                <PackageIcon strokeWidth={1} size={28} color={theme.palette.text.disabled} />
                <Stack spacing={0.5} alignItems="center">
                  <Typography variant="body3">No package scanned yet</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.5 }}>
                    {isResolvingScan
                      ? 'Reading package…'
                      : "Scan a box's handle label to assign it to this product."}
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={2}>
                {scannedPackages.map((pkg) => (
                  <Stack key={pkg.id} spacing={2}>
                    {/* The scanned package rendered as its shared PackageCell preview
                        (product image + name + SKU) so the operator can confirm the
                        box's contents match the product being packed. A red border +
                        warning icon flag a product mismatch (still assignable). */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{
                        border: `1px solid ${pkg.mismatch ? theme.palette.warning.main : theme.palette.primary[100]}`,
                        borderRadius: 2,
                        px: 4,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* size=64 matches ProductPreview's default (size="large") image
                            footprint above, so Product and Package cards read as the same
                            scale. */}
                        <PackageCell pkg={pkg.pkg ?? { name: pkg.name }} size={64} />
                      </Box>
                      {pkg.mismatch && (
                        <TriangleAlertIcon strokeWidth={1.5} size={18} color={theme.palette.warning.main} />
                      )}
                      {/* "Change" clears this scanned package so the operator can
                          scan a different box — parity with the shared Preview's
                          onChange affordance (e.g. the Product card above). */}
                      <Button
                        variant="tertiary"
                        onClick={() => handleRemoveScanned(pkg.id)}
                      >
                        Change
                      </Button>
                    </Stack>
                    {pkg.mismatch && (
                      <Alert
                        severity="warning"
                        description={`This package is registered to ${pkg.productTitle ?? 'a different product'}${
                          expectedProduct?.title ? `, not ${expectedProduct.title}` : ''
                        }. Double-check before assigning.`}
                      />
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={4}>
          {/* VERA-891 Slice D: manual (existing) vs NFC-scan (stub) method toggle —
              preserved verbatim for the order-level bulk-link flow (OrdersPage),
              which isn't in scope for the VRG-140 Slice 2 scan-driven rework. */}
          <SegmentedTabs
            fullWidth
            tabs={[
              { label: 'Select manually', value: 'manual' },
              { label: 'Scan NFC tag', value: 'nfc' },
            ]}
            value={linkMethod}
            onChange={(value) => setLinkMethod(value as 'manual' | 'nfc')}
          />

          {linkMethod === 'nfc' ? (
            // TODO(VERA-790): wire NFC-encoder scan-session once VERA-790 lands.
            <EmptyState
              icon={<SmartphoneNfcIcon strokeWidth={1} size={32} color={theme.palette.primary.main} />}
              title="NFC scan coming soon"
              text="Scanning a package's NFC tag to link it to this order isn't available yet. Use manual selection for now."
            />
          ) : (
            <>
              <Search
                value={search}
                onChange={setSearch}
                onClear={() => setSearch('')}
                placeholder="Search packages"
                fullWidth
                debounce={false}
              />

              {linkPackages.isError && (
                <Alert severity="error" description={getErrorMessage(linkPackages.error)} />
              )}

              <Box
                sx={{
                  maxHeight: 360,
                  overflowY: 'auto',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                }}
              >
                {isLoadingPackages ? (
                  <Stack spacing={2} sx={{ p: 3 }}>
                    <Skeleton height={40} />
                    <Skeleton height={40} />
                    <Skeleton height={40} />
                  </Stack>
                ) : packages.length === 0 ? (
                  <EmptyState
                    icon={<PackageIcon strokeWidth={1} size={32} color={theme.palette.primary.main} />}
                    title="No packages found"
                    text={
                      search
                        ? 'No packages match your search.'
                        : 'This company has no packages available to link.'
                    }
                  />
                ) : (
                  packages.map((pkg, index) => (
                    <Stack
                      key={pkg.id}
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      onClick={() => toggleSelected(pkg.id)}
                      sx={{
                        px: 3,
                        py: 2,
                        cursor: 'pointer',
                        borderBottom:
                          index < packages.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      {singleSelect ? (
                        <Radio
                          checked={selectedIds.has(pkg.id)}
                          onChange={() => toggleSelected(pkg.id)}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                        />
                      ) : (
                        <Checkbox
                          checked={selectedIds.has(pkg.id)}
                          onChange={() => toggleSelected(pkg.id)}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                        />
                      )}
                      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {pkg.name}
                        </Typography>
                        <Typography variant="body3" color="text.secondary" noWrap>
                          {pkg.tag_shop_product?.sku ? `SKU ${pkg.tag_shop_product.sku} · ` : ''}
                          Qty {pkg.quantity}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))
                )}
              </Box>
            </>
          )}
        </Stack>
      )}
    </Modal>
  )
}
